import { api } from "@/api/client";
import type {
  CashflowCalendarView,
  CashflowCategory,
  CashflowManualItemIn,
  CashflowManualItemsBulkIn,
  CashflowManualItemsBulkResult,
  CashflowManualItemView,
  CashflowManualSeriesIn,
  CashflowManualSeriesView,
  CashflowReceivablesCheck,
  CashflowSalaryImportResult,
  CashflowSettingsUpdate,
  CashflowSettingsView,
} from "@/types/api";

export async function getCashflowSettings(): Promise<CashflowSettingsView> {
  const { data } = await api.get<CashflowSettingsView>("/cashflow/settings");
  return data;
}

export async function updateCashflowSettings(
  body: CashflowSettingsUpdate,
): Promise<CashflowSettingsView> {
  const { data } = await api.put<CashflowSettingsView>("/cashflow/settings", body);
  return data;
}

export async function listCashflowCategories(): Promise<CashflowCategory[]> {
  const { data } = await api.get<CashflowCategory[]>("/cashflow/categories");
  return data;
}

/**
 * Календарь на дату; `?connection_id` подмешивает интерцептор axios.
 *
 * Пустую дату не отправляем вовсе (`as_of=` бэк не разберёт) — без параметра
 * он считает на сегодня.
 */
export async function getCashflowCalendar(asOf?: string): Promise<CashflowCalendarView> {
  const params: Record<string, string> = {};
  if (asOf) params.as_of = asOf;
  const { data } = await api.get<CashflowCalendarView>("/cashflow/calendar", { params });
  return data;
}

/**
 * Сверка «дебиторка vs план прихода» на дату — отдельный тяжёлый запрос.
 *
 * Панель грузит её только по раскрытию: бэк читает регистр расчётов и планы
 * поступлений по всем организациям, это несколько секунд.
 */
export async function getCashflowReceivablesCheck(
  asOf?: string,
): Promise<CashflowReceivablesCheck> {
  const params: Record<string, string> = {};
  if (asOf) params.as_of = asOf;
  const { data } = await api.get<CashflowReceivablesCheck>("/cashflow/receivables-check", {
    params,
  });
  return data;
}

// --- ручные серии ---

export async function listCashflowSeries(): Promise<CashflowManualSeriesView[]> {
  const { data } = await api.get<CashflowManualSeriesView[]>("/cashflow/manual/series");
  return data;
}

export async function createCashflowSeries(
  body: CashflowManualSeriesIn,
): Promise<CashflowManualSeriesView> {
  const { data } = await api.post<CashflowManualSeriesView>("/cashflow/manual/series", body);
  return data;
}

export async function updateCashflowSeries(
  id: number,
  body: CashflowManualSeriesIn,
): Promise<CashflowManualSeriesView> {
  const { data } = await api.put<CashflowManualSeriesView>(`/cashflow/manual/series/${id}`, body);
  return data;
}

export async function deleteCashflowSeries(id: number): Promise<void> {
  await api.delete(`/cashflow/manual/series/${id}`);
}

// --- ручные строки ---

export async function listCashflowItems(range?: {
  dateFrom?: string;
  dateTo?: string;
}): Promise<CashflowManualItemView[]> {
  const params: Record<string, string> = {};
  if (range?.dateFrom) params.date_from = range.dateFrom;
  if (range?.dateTo) params.date_to = range.dateTo;
  const { data } = await api.get<CashflowManualItemView[]>("/cashflow/manual/items", { params });
  return data;
}

export async function createCashflowItem(
  body: CashflowManualItemIn,
): Promise<CashflowManualItemView> {
  const { data } = await api.post<CashflowManualItemView>("/cashflow/manual/items", body);
  return data;
}

export async function updateCashflowItem(
  id: number,
  body: CashflowManualItemIn,
): Promise<CashflowManualItemView> {
  const { data } = await api.put<CashflowManualItemView>(`/cashflow/manual/items/${id}`, body);
  return data;
}

export async function deleteCashflowItem(id: number): Promise<void> {
  await api.delete(`/cashflow/manual/items/${id}`);
}

export async function createCashflowItemsBulk(
  body: CashflowManualItemsBulkIn,
): Promise<CashflowManualItemsBulkResult> {
  const { data } = await api.post<CashflowManualItemsBulkResult>(
    "/cashflow/manual/items/bulk",
    body,
  );
  return data;
}

// --- импорт зарплаты из Google «Свод» ---

/**
 * Импорт зарплатного плана: три серии на организацию, идемпотентно.
 *
 * Пустую дату не отправляем вовсе (`as_of=` бэк не разберёт) — без параметра
 * он считает на сегодня.
 */
export async function importSalary(asOf?: string): Promise<CashflowSalaryImportResult> {
  const params: Record<string, string> = {};
  if (asOf) params.as_of = asOf;
  const { data } = await api.post<CashflowSalaryImportResult>(
    "/cashflow/salary/import",
    null,
    { params },
  );
  return data;
}
